
import React from 'react';
import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  link: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, icon: Icon, link }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  return (
    <div className="service-card h-full flex flex-col backdrop-blur-lg bg-black/70 border border-purple-500/30 rounded-xl p-6 transition-all duration-500 hover:transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(139,92,246,0.6)]">
      <div className="p-4 mb-4 bg-futurelevel-purple/20 rounded-full w-16 h-16 flex items-center justify-center relative overflow-hidden group-hover:bg-futurelevel-purple/30 transition-all duration-300">
        <Icon size={32} className="text-purple-300 relative z-10 group-hover:text-purple-200 transition-colors duration-300 animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-purple-700/20 to-cyan-500/20 opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/30 to-cyan-400/30 rounded-full blur opacity-70 group-hover:opacity-100 transition-all duration-700 animate-pulse"></div>
      </div>
      
      <h3 className="text-xl font-semibold mb-3 font-future tracking-wide text-white group-hover:text-purple-300 transition-colors duration-300">{title}</h3>
      <p className="text-gray-300 mb-6 flex-grow">{description}</p>
      
      <Link 
        to={link} 
        className="text-purple-300 hover:text-purple-200 font-medium flex items-center mt-auto relative overflow-hidden group-hover:pl-1 transition-all duration-300"
      >
        <span className="relative inline-block">{t.learnMore}</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor" 
          className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-300 group-hover:text-purple-300"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 5l7 7-7 7" 
          />
        </svg>
        <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-purple-400 group-hover:w-full transition-all duration-300"></span>
      </Link>

      {/* Tech grid corner decoration */}
      <div className="absolute top-0 right-0 w-12 h-12 pointer-events-none opacity-30 group-hover:opacity-60 transition-opacity duration-500">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
          {Array(9).fill(0).map((_, i) => (
            <div 
              key={i} 
              className="border-[0.5px] border-purple-500/70"
              style={{
                animation: `pulse ${Math.random() * 3 + 2}s infinite`
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
